import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
  updateDoc,
  getDoc,
  limit,
} from 'firebase/firestore'
import { db } from '../../firebase'
import { validateRange, ymdLocal } from '../utils/datetime'
import { localISOToUtcDate, LOCAL_TZ } from '../utils/time'
import type { Lesson, NewLesson, CreateLessonInput } from '../types/lesson'
import {
  createLessonCancelledNotification,
  createLessonRescheduledNotification,
} from '../../services/notificationService'

// SPAM-CLICK PROTECTION: Lock flag to prevent concurrent lesson creation
let isCreatingLesson = false

// Debug flag
export const DEBUG_CALENDAR = process.env.NODE_ENV === 'development'
export type LessonInput = {
  startAt: string // UTC ISO
  endAt: string // UTC ISO
  timezone: string // IANA timezone
  teacherId: string
  title?: string
  instrument?: string
  color?: string
  studentId?: string | null
  studentName?: string
  additionalStudents?: any[]
  status?: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELED'
  notes?: string
  location?: string
  durationMinutes: number
  lessonType?:
    | 'Private Lesson'
    | 'Group Lesson'
    | 'Trial Lesson'
    | 'Masterclass'
    | null
}
// P0 SCALABILITY FIX: Add query limit constant
const LESSON_QUERY_LIMIT = 100 // Maximum lessons to fetch per query (updated for busy teachers)
/**
 * Subscribe to lessons for a teacher within a date range
 * Collection: teachers/{teacherId}/calendar (subcollection)
 * Query: startAt range with LIMIT
 *
 * P0 FIX: Added limit() to prevent unbounded queries
 */
export function subscribeLessons({
  teacherId,
  from,
  to,
  onData,
}: {
  teacherId: string
  from: string
  to: string
  onData: (lessons: Lesson[]) => void
}): () => void {
  console.log('🔍 [QUERY/LISTENER] Subscribing to lessons with LIMIT:', {
    teacherId,
    from,
    to,
    limit: LESSON_QUERY_LIMIT,
    path: `teachers/${teacherId}/calendar`,
  })

  // FIX 1: Convert ISO strings to Firestore Timestamps for proper comparison
  const fromTimestamp = Timestamp.fromDate(new Date(from))
  const toTimestamp = Timestamp.fromDate(new Date(to))

  console.log('🔍 [FIX 1] Converted to Timestamps:', {
    fromISO: from,
    toISO: to,
    fromTimestamp: fromTimestamp.toDate().toISOString(),
    toTimestamp: toTimestamp.toDate().toISOString(),
  })

  // Query the teacher's calendar subcollection
  const teacherRef = doc(db, 'teachers', teacherId)
  const calendarCol = collection(teacherRef, 'calendar')

  // P0 FIX: Add limit() to query to prevent unbounded reads
  const q = query(
    calendarCol,
    where('startAt', '>=', fromTimestamp),
    where('startAt', '<=', toTimestamp),
    orderBy('startAt', 'asc'),
    limit(LESSON_QUERY_LIMIT), // ✅ CRITICAL: Limit query results
  )

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      // PROOF LOG: Snapshot size
      console.log('✅ [P0 FIX] Snapshot size (limited):', {
        size: snapshot.size,
        limit: LESSON_QUERY_LIMIT,
        hitLimit: snapshot.size === LESSON_QUERY_LIMIT,
      })

      if (snapshot.size > 0) {
        // PROOF LOG: First doc details
        const firstDoc = snapshot.docs[0]
        const firstData = firstDoc.data()
        console.log('✅ [FIX 1 PROOF] First doc:', {
          id: firstDoc.id,
          startAt:
            firstData.startAt?.toDate?.()?.toISOString() || firstData.startAt,
          durationMinutes: firstData.durationMinutes,
        })
      }

      console.log('🔍 [QUERY/LISTENER] Snapshot received:', {
        size: snapshot.size,
        empty: snapshot.empty,
        metadata: {
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
          fromCache: snapshot.metadata.fromCache,
        },
      })

      const lessons = snapshot.docs.map((doc) => {
        const data = doc.data()
        // Convert Firestore Timestamps to ISO strings
        const startAt = data.startAt?.toDate?.()
          ? data.startAt.toDate().toISOString()
          : data.startAt
        const endAt = data.endAt?.toDate?.()
          ? data.endAt.toDate().toISOString()
          : data.endAt
        return {
          id: doc.id,
          ...data,
          startAt,
          endAt,
        } as Lesson
      })

      console.log('🔍 [QUERY/LISTENER] Converted lessons:', {
        count: lessons.length,
        firstThree: lessons.slice(0, 3).map((l) => ({
          id: l.id,
          startAt: l.startAt,
          startAtType: typeof l.startAt,
          durationMinutes: l.durationMinutes,
          teacherId: l.teacherId,
          timezone: l.timezone,
        })),
      })

      // Validate lessons have required fields
      const validLessons = lessons.filter((lesson) => {
        const isValid = lesson.startAt && lesson.endAt && lesson.timezone
        if (!isValid) {
          console.warn('🔍 [QUERY/LISTENER] Invalid lesson filtered out:', {
            id: lesson.id,
            hasStartAt: !!lesson.startAt,
            hasEndAt: !!lesson.endAt,
            hasTimezone: !!lesson.timezone,
          })
        }
        return isValid
      })

      console.log('🔍 [QUERY/LISTENER] Valid lessons after filtering:', {
        validCount: validLessons.length,
        invalidCount: lessons.length - validLessons.length,
      })

      onData(validLessons)
    },
    (error) => {
      console.error('🔍 [QUERY/LISTENER] Subscription error:', error)
    },
  )

  return unsubscribe
}
/**
 * Get lessons by date range (one-time query, not subscription)
 * Collection: lessons (root level)
 * Query: teacherId + startAt range
 * @param params - Query parameters
 * @returns Promise resolving to array of lessons
 */
export async function getLessonsByDateRange({
  startLocalISO,
  endLocalISO,
  teacherId,
}: {
  startLocalISO: string
  endLocalISO: string
  teacherId?: string
}): Promise<Lesson[]> {
  if (DEBUG_CALENDAR) {
    console.log('[LESSONS_SERVICE] Getting lessons by date range:', {
      startLocalISO,
      endLocalISO,
      teacherId,
    })
  }
  try {
    // Convert local ISO strings to UTC Date objects
    const startUtc = localISOToUtcDate(startLocalISO, LOCAL_TZ)
    const endUtc = localISOToUtcDate(endLocalISO, LOCAL_TZ)
    // Convert to ISO strings for Firestore query
    const startUtcISO = startUtc.toISOString()
    const endUtcISO = endUtc.toISOString()
    // Build query
    const lessonsRef = collection(db, 'lessons')
    let q = query(
      lessonsRef,
      where('startAt', '>=', startUtcISO),
      where('startAt', '<=', endUtcISO),
      orderBy('startAt', 'asc'),
    )
    // Add teacherId filter if provided
    if (teacherId) {
      q = query(
        lessonsRef,
        where('teacherId', '==', teacherId),
        where('startAt', '>=', startUtcISO),
        where('startAt', '<=', endUtcISO),
        orderBy('startAt', 'asc'),
      )
    }
    // Execute query
    const snapshot = await getDocs(q)
    // Map documents to Lesson objects
    const lessons = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Lesson,
    )
    if (DEBUG_CALENDAR) {
      console.log(
        `[LESSONS_SERVICE] Retrieved ${lessons.length} lessons from Firestore`,
      )
    }
    // Validate lessons have required fields
    const validLessons = lessons.filter((lesson) => {
      const isValid = lesson.startAt && lesson.endAt && lesson.timezone
      if (!isValid && DEBUG_CALENDAR) {
        console.warn(
          `[LESSONS_SERVICE] Invalid lesson ${lesson.id}:`,
          'missing required fields',
        )
      }
      return isValid
    })
    return validLessons
  } catch (error) {
    console.error('[LESSONS_SERVICE] Error getting lessons:', error)
    throw error
  }
}
/**
 * Check if a duplicate lesson exists for the same student at the same time
 * @param teacherId - Teacher ID
 * @param studentId - Student ID
 * @param startAt - Lesson start time (Date object)
 * @returns Promise<boolean> - true if duplicate exists
 */
export async function checkForDuplicateLesson(
  teacherId: string,
  studentId: string,
  startAt: Date,
): Promise<boolean> {
  console.log('[LESSONS_SERVICE] Checking for duplicate lesson:', {
    teacherId,
    studentId,
    startAt: startAt.toISOString(),
  })

  try {
    const teacherRef = doc(db, 'teachers', teacherId)
    const calendarCol = collection(teacherRef, 'calendar')

    // Convert Date to Timestamp for Firestore query
    const startTimestamp = Timestamp.fromDate(startAt)

    // Query for lessons with same student and start time
    const q = query(
      calendarCol,
      where('studentId', '==', studentId),
      where('startAt', '==', startTimestamp),
    )

    const snapshot = await getDocs(q)

    const hasDuplicate = !snapshot.empty

    console.log('[LESSONS_SERVICE] Duplicate check result:', {
      hasDuplicate,
      matchingLessons: snapshot.size,
    })

    return hasDuplicate
  } catch (error) {
    console.error('[LESSONS_SERVICE] Error checking for duplicate:', error)
    // On error, allow creation (fail open)
    return false
  }
}
/**
 * Create a new lesson in Firestore
 * Collection: teachers/{teacherId}/calendar (subcollection)
 * Returns: Document ID
 *
 * @param input - Lesson creation input with local Date objects
 * @returns Promise resolving to the created document ID
 */
export async function createLesson(input: CreateLessonInput): Promise<string> {
  // SPAM-CLICK PROTECTION: Check if lesson creation is already in progress
  if (isCreatingLesson) {
    throw new Error('Lesson creation already in progress. Please wait.')
  }

  // SPAM-CLICK PROTECTION: Set lock before starting
  isCreatingLesson = true

  try {
    console.log('[LESSONS_SERVICE] createLesson called with input:', {
      ...input,
      startAt:
        input.startAt instanceof Date
          ? {
              iso: input.startAt.toISOString(),
              local: input.startAt.toLocaleString(),
              year: input.startAt.getFullYear(),
              month: input.startAt.getMonth() + 1,
              date: input.startAt.getDate(),
              hours: input.startAt.getHours(),
              minutes: input.startAt.getMinutes(),
            }
          : input.startAt,
    })

    // Validate required fields
    if (
      !input.title ||
      !input.studentId ||
      !input.teacherId ||
      !input.startAt ||
      !input.durationMin
    ) {
      const error = new Error(
        'Lesson requires: title, studentId, teacherId, startAt, durationMin',
      )
      console.error('[LESSONS_SERVICE] Validation error:', error)
      throw error
    }

    // Ensure startAt is a Date object
    let startAtDate: Date
    if (input.startAt instanceof Date) {
      startAtDate = input.startAt
      console.log('[LESSONS_SERVICE] startAt is already a Date object')
    } else if (typeof input.startAt === 'string') {
      startAtDate = new Date(input.startAt)
      console.log('[LESSONS_SERVICE] Converted startAt from string to Date')
    } else {
      throw new Error('startAt must be a Date object or ISO string')
    }

    // Validate the Date object is valid
    if (isNaN(startAtDate.getTime())) {
      throw new Error('Invalid startAt date')
    }

    console.log('[LESSONS_SERVICE] Using startAt Date:', {
      iso: startAtDate.toISOString(),
      local: startAtDate.toLocaleString('en-US', { timeZone: input.timezone }),
      year: startAtDate.getFullYear(),
      month: startAtDate.getMonth() + 1,
      date: startAtDate.getDate(),
      hours: startAtDate.getHours(),
      minutes: startAtDate.getMinutes(),
      timezone: input.timezone,
    })

    // CHECK FOR DUPLICATE LESSON
    const isDuplicate = await checkForDuplicateLesson(
      input.teacherId,
      input.studentId,
      startAtDate,
    )

    if (isDuplicate) {
      const error = new Error(
        'This student already has a lesson scheduled at this time. Please choose a different time.',
      )
      error.name = 'DuplicateLessonError'
      console.error('[LESSONS_SERVICE] Duplicate lesson detected:', {
        teacherId: input.teacherId,
        studentId: input.studentId,
        startAt: startAtDate.toISOString(),
      })
      throw error
    }

    // Compute endAt from startAt + durationMin
    const endAt = new Date(startAtDate.getTime() + input.durationMin * 60000)
    console.log('[LESSONS_SERVICE] Calculated endAt:', {
      iso: endAt.toISOString(),
      local: endAt.toLocaleString('en-US', { timeZone: input.timezone }),
    })

    // Convert to UTC ISO strings for logging
    const startAtUtc = startAtDate.toISOString()
    const endAtUtc = endAt.toISOString()
    console.log('[LESSONS_SERVICE] UTC times for validation:', {
      startAtUtc,
      endAtUtc,
      timezone: input.timezone,
    })

    // Validate the time range
    validateRange(startAtUtc, endAtUtc)

    // Target: teachers/{teacherId}/calendar
    const teacherRef = doc(db, 'teachers', input.teacherId)
    const calendarCol = collection(teacherRef, 'calendar')

    // Prepare lesson data - store Date objects directly
    // Firestore will convert them to Timestamps automatically
    const payload = {
      teacherId: input.teacherId,
      studentId: input.studentId,
      studentName: input.studentName,
      title: input.title,
      startAt: startAtDate, // Firestore will convert to Timestamp
      endAt: endAt, // Firestore will convert to Timestamp
      durationMinutes: input.durationMin,
      location: input.location ?? null,
      instrument: input.instrument ?? null,
      notes: input.notes ?? null,
      timezone: input.timezone,
      lessonType: input.lessonType || null,
      status: 'SCHEDULED', // ✅ FIXED: Default status for new lessons (standardized)
      cancelled: false, // ADDED: Default cancelled state
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    console.log('[LESSONS_SERVICE] Payload to store in Firestore:', {
      ...payload,
      startAt: {
        type: 'Date',
        iso: startAtDate.toISOString(),
        local: startAtDate.toLocaleString('en-US', {
          timeZone: input.timezone,
        }),
        willBecomeTimestamp: true,
      },
      endAt: {
        type: 'Date',
        iso: endAt.toISOString(),
        local: endAt.toLocaleString('en-US', { timeZone: input.timezone }),
        willBecomeTimestamp: true,
      },
    })

    const docRef = await addDoc(calendarCol, payload)
    console.log('[LESSONS_SERVICE] ✅ Lesson created successfully!')
    console.log('[LESSONS_SERVICE] Document ID:', docRef.id)
    console.log(
      '[LESSONS_SERVICE] Path:',
      `teachers/${input.teacherId}/calendar/${docRef.id}`,
    )
    console.log(
      '[LESSONS_SERVICE] Expected date key:',
      ymdLocal(startAtUtc, input.timezone),
    )
    return docRef.id
  } catch (error) {
    console.error('[LESSONS_SERVICE] ❌ Error creating lesson:', error)
    throw error
  } finally {
    // SPAM-CLICK PROTECTION: Always release lock, even on error
    isCreatingLesson = false
  }
}
/**
 * Legacy function for backward compatibility
 * @deprecated Use createLesson instead
 */
export async function createLessonFromLocal(input: NewLesson): Promise<string> {
  if (DEBUG_CALENDAR) {
    console.log('[LESSONS_SERVICE] Creating lesson from local time (legacy):', {
      teacherId: input.teacherId,
      startLocalISO: input.startLocalISO,
      endLocalISO: input.endLocalISO,
      status: input.status,
    })
  }
  // Validate required fields
  if (
    !input.startLocalISO ||
    !input.endLocalISO ||
    !input.teacherId ||
    !input.durationMinutes
  ) {
    throw new Error(
      'Lesson requires: startLocalISO, endLocalISO, teacherId, durationMinutes',
    )
  }
  // Convert local ISO strings to UTC
  const startUtc = localISOToUtcDate(input.startLocalISO, LOCAL_TZ)
  const endUtc = localISOToUtcDate(input.endLocalISO, LOCAL_TZ)
  const startAtUtc = startUtc.toISOString()
  const endAtUtc = endUtc.toISOString()
  // Validate the time range
  validateRange(startAtUtc, endAtUtc)
  // Prepare lesson data with UTC times
  const lessonsRef = collection(db, 'lessons')
  const lessonData = {
    teacherId: input.teacherId,
    studentId: input.studentId || null,
    title: input.title,
    startAt: startAtUtc,
    endAt: endAtUtc,
    timezone: LOCAL_TZ,
    durationMinutes: input.durationMinutes,
    instrument: input.instrument,
    status: input.status || 'SCHEDULED',
    location: input.location,
    notes: input.notes,
    color: input.color,
    studentName: input.studentName,
    lessonType: input.lessonType,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
  try {
    const docRef = await addDoc(lessonsRef, lessonData)
    if (DEBUG_CALENDAR) {
      console.log(`[LESSONS_SERVICE] Created lesson with ID: ${docRef.id}`)
    }
    return docRef.id
  } catch (error) {
    console.error('[LESSONS_SERVICE] Create error:', error)
    throw error
  }
}
/**
 * Update an existing lesson in Firestore
 * Collection: teachers/{teacherId}/calendar/{lessonId}
 *
 * @param lessonId - The ID of the lesson to update
 * @param teacherId - The ID of the teacher who owns the lesson
 * @param updates - Partial lesson object with fields to update
 * @returns Promise that resolves when update is complete
 */
export async function updateLesson(
  lessonId: string,
  teacherId: string,
  updates: Partial<Lesson>,
): Promise<void> {
  console.log('[LESSONS_SERVICE] 🔧 updateLesson called with:', {
    lessonId,
    teacherId,
    updates,
  })

  // Validate required parameters
  if (!lessonId || !teacherId) {
    const error = new Error('Lesson ID and Teacher ID are required')
    console.error('[LESSONS_SERVICE] ❌ Validation error:', error)
    throw error
  }

  // Validate that we're not trying to update immutable fields
  const immutableFields = ['id', 'createdAt']
  const invalidFields = Object.keys(updates).filter((key) =>
    immutableFields.includes(key),
  )
  if (invalidFields.length > 0) {
    const error = new Error(
      `Cannot update immutable fields: ${invalidFields.join(', ')}`,
    )
    console.error('[LESSONS_SERVICE] ❌ Validation error:', error)
    throw error
  }

  try {
    // Reference to the lesson document in the calendar subcollection
    const teacherRef = doc(db, 'teachers', teacherId)
    const lessonRef = doc(teacherRef, 'calendar', lessonId)
    console.log('[LESSONS_SERVICE] 📍 Document path:', {
      path: `teachers/${teacherId}/calendar/${lessonId}`,
      teacherRef: teacherRef.path,
      lessonRef: lessonRef.path,
    })

    // CRITICAL FIX: Get current lesson data BEFORE updating for notification comparison
    const currentLessonSnap = await getDoc(lessonRef)
    const currentLesson = currentLessonSnap.exists()
      ? currentLessonSnap.data()
      : null

    console.log('[LESSONS_SERVICE] 📖 Current lesson data fetched:', {
      exists: currentLessonSnap.exists(),
      hasData: !!currentLesson,
      currentStatus: currentLesson?.status,
    })

    // ATOMIC MERGE: Prepare update payload
    const updatePayload: any = {
      ...updates,
      updatedAt: serverTimestamp(),
    }

    // Convert Date objects or ISO strings to Firestore Timestamps if needed
    if (updates.startAt) {
      if (typeof updates.startAt === 'string') {
        updatePayload.startAt = new Date(updates.startAt)
        console.log('[LESSONS_SERVICE] 🕐 startAt conversion:', {
          original: updates.startAt,
          converted: updatePayload.startAt,
          iso: updatePayload.startAt.toISOString(),
        })
      } else if (updates.startAt instanceof Date) {
        updatePayload.startAt = updates.startAt
        console.log('[LESSONS_SERVICE] 🕐 startAt (Date object):', {
          date: updatePayload.startAt,
          iso: updatePayload.startAt.toISOString(),
        })
      }
    }

    if (updates.endAt) {
      if (typeof updates.endAt === 'string') {
        updatePayload.endAt = new Date(updates.endAt)
        console.log('[LESSONS_SERVICE] 🕐 endAt conversion:', {
          original: updates.endAt,
          converted: updatePayload.endAt,
          iso: updatePayload.endAt.toISOString(),
        })
      } else if (updates.endAt instanceof Date) {
        updatePayload.endAt = updates.endAt
        console.log('[LESSONS_SERVICE] 🕐 endAt (Date object):', {
          date: updatePayload.endAt,
          iso: updatePayload.endAt.toISOString(),
        })
      }
    }

    // Validate time range if both startAt and endAt are present
    if (updatePayload.startAt && updatePayload.endAt) {
      const startTime =
        updatePayload.startAt instanceof Date
          ? updatePayload.startAt.getTime()
          : new Date(updatePayload.startAt).getTime()
      const endTime =
        updatePayload.endAt instanceof Date
          ? updatePayload.endAt.getTime()
          : new Date(updatePayload.endAt).getTime()
      if (startTime >= endTime) {
        throw new Error('Start time must be before end time')
      }
      console.log('[LESSONS_SERVICE] ⏰ Time range validation:', {
        startTime,
        endTime,
        durationMs: endTime - startTime,
        durationMinutes: (endTime - startTime) / (1000 * 60),
      })
    }

    // Normalize status to uppercase if present
    if (updates.status) {
      updatePayload.status = String(updates.status).toUpperCase()
    }

    console.log('[LESSONS_SERVICE] 📦 Final update payload:', {
      ...updatePayload,
      startAt:
        updatePayload.startAt instanceof Date
          ? updatePayload.startAt.toISOString()
          : updatePayload.startAt,
      endAt:
        updatePayload.endAt instanceof Date
          ? updatePayload.endAt.toISOString()
          : updatePayload.endAt,
    })

    // ATOMIC UPDATE: Update the document in Firestore
    console.log('[LESSONS_SERVICE] 🚀 Executing Firestore update...')
    await updateDoc(lessonRef, updatePayload)
    console.log('[LESSONS_SERVICE] ✅ Firestore update completed!')

    // Create notifications for status changes (using data fetched BEFORE update)
    if (currentLesson) {
      // Check for cancellation
      if (
        updates.status === 'CANCELED' &&
        currentLesson.status !== 'CANCELED'
      ) {
        console.log('[LESSONS_SERVICE] 📢 Creating cancellation notification')
        await createLessonCancelledNotification(teacherId, {
          lessonId,
          studentId: currentLesson.studentId,
          studentName: currentLesson.studentName,
          title: currentLesson.title,
          startAt: currentLesson.startAt?.toDate?.() || currentLesson.startAt,
        }).catch((err) =>
          console.error('[LESSONS_SERVICE] Notification error:', err),
        )
      }

      // Check for reschedule (startAt changed but not cancelled)
      if (
        updates.startAt &&
        updates.status !== 'CANCELED' &&
        currentLesson.status !== 'CANCELED'
      ) {
        const oldStartAt =
          currentLesson.startAt?.toDate?.() || new Date(currentLesson.startAt)
        const newStartAt =
          updatePayload.startAt instanceof Date
            ? updatePayload.startAt
            : new Date(updatePayload.startAt)

        // Only create notification if times are actually different (more than 1 minute difference)
        if (Math.abs(oldStartAt.getTime() - newStartAt.getTime()) > 60000) {
          console.log('[LESSONS_SERVICE] 📢 Creating reschedule notification')
          await createLessonRescheduledNotification(teacherId, {
            lessonId,
            studentId: currentLesson.studentId,
            studentName: currentLesson.studentName,
            title: currentLesson.title,
            oldStartAt,
            newStartAt,
          }).catch((err) =>
            console.error('[LESSONS_SERVICE] Notification error:', err),
          )
        }
      }
    }

    // Verify the stored value by reading it back
    const docSnap = await getDoc(lessonRef)
    if (docSnap.exists()) {
      const storedData = docSnap.data()
      console.log('[LESSONS_SERVICE] 📖 Verified stored values in Firestore:', {
        startAt:
          storedData.startAt?.toDate?.()?.toISOString() || storedData.startAt,
        endAt: storedData.endAt?.toDate?.()?.toISOString() || storedData.endAt,
        status: storedData.status,
        cancelReason: storedData.cancelReason,
        cancelReasonNote: storedData.cancelReasonNote,
        cancelledAt: storedData.cancelledAt,
      })
    } else {
      console.warn('[LESSONS_SERVICE] ⚠️ Document not found after update')
    }
  } catch (error) {
    console.error('[LESSONS_SERVICE] ❌ Error updating lesson:', error)
    console.error('[LESSONS_SERVICE] Failed update details:', {
      lessonId,
      teacherId,
      updates,
    })
    throw error
  }
}

/**
 * Mark a lesson as completed with attendance tracking
 * Sets completedAt, attended, and ensures cancelled is false
 */
export async function completeLesson(
  teacherId: string,
  lessonId: string,
  attended: boolean = true,
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  try {
    const lessonRef = doc(db, 'users', teacherId, 'lessons', lessonId)

    // Ensure consistency: completed lessons cannot be cancelled
    await updateDoc(lessonRef, {
      status: 'COMPLETED',
      completedAt: new Date().toISOString(),
      attended: attended,
      cancelled: false,
      updatedAt: new Date().toISOString(),
    })

    console.log(
      `Lesson ${lessonId} marked as completed (attended: ${attended})`,
    )
  } catch (error) {
    console.error('Error completing lesson:', error)
    throw new Error(`Failed to complete lesson: ${error.message}`)
  }
}

/**
 * Cancel a lesson with optional reason
 * Sets cancelled, clears completedAt, and ensures attended is false
 */
export async function cancelLesson(
  teacherId: string,
  lessonId: string,
  cancelReason?: string,
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  try {
    const lessonRef = doc(db, 'users', teacherId, 'lessons', lessonId)

    // Ensure consistency: cancelled lessons cannot be completed or attended
    const updateData: any = {
      status: 'CANCELED',
      cancelled: true,
      attended: false,
      completedAt: null, // Clear any previous completion timestamp
      updatedAt: new Date().toISOString(),
    }

    // Only add cancelReason if provided
    if (cancelReason && cancelReason.trim() !== '') {
      updateData.cancelReason = cancelReason.trim()
    }

    await updateDoc(lessonRef, updateData)

    console.log(
      `Lesson ${lessonId} cancelled${cancelReason ? ` (reason: ${cancelReason})` : ''}`,
    )
  } catch (error) {
    console.error('Error cancelling lesson:', error)
    throw new Error(`Failed to cancel lesson: ${error.message}`)
  }
}

/**
 * Update lesson status with proper completion/cancellation tracking
 * This is a helper that routes to the appropriate function based on status
 */
export async function updateLessonStatus(
  teacherId: string,
  lessonId: string,
  status: LessonStatus,
  options?: {
    attended?: boolean
    cancelReason?: string
  },
): Promise<void> {
  if (!teacherId || !lessonId) {
    throw new Error('Teacher ID and Lesson ID are required')
  }

  try {
    if (status === 'COMPLETED') {
      // Use completeLesson for proper tracking
      await completeLesson(teacherId, lessonId, options?.attended ?? true)
    } else if (status === 'CANCELED') {
      // Use cancelLesson for proper tracking
      await cancelLesson(teacherId, lessonId, options?.cancelReason)
    } else {
      // For other statuses (SCHEDULED, RESCHEDULED), just update status
      const lessonRef = doc(db, 'users', teacherId, 'lessons', lessonId)
      await updateDoc(lessonRef, {
        status: status,
        updatedAt: new Date().toISOString(),
      })
    }

    console.log(`Lesson ${lessonId} status updated to ${status}`)
  } catch (error) {
    console.error('Error updating lesson status:', error)
    throw new Error(`Failed to update lesson status: ${error.message}`)
  }
}
